import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ValidatorsService } from 'src/app/services/validators.service';
import { ProfileService } from 'src/app/pages/management/services/profile.service';

@Component({
  selector: 'app-subscription-alert-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './subscription-alert-banner.component.html',
  styleUrls: ['./subscription-alert-banner.component.scss']
})
export class SubscriptionAlertBannerComponent {
  private validatorsService = inject(ValidatorsService);
  private profileService = inject(ProfileService);

  user = this.validatorsService.user;
  company = this.validatorsService.company;

  isSuperAdmin = computed(() => {
    return this.user()?.role?.name === 'SUPER_ADMIN';
  });

  isSuspended = computed(() => {
    return !!this.company()?.customerSubscription?.isSuspended;
  });

  // ─── 1. SUSCRIPCIÓN COMERCIAL ────────────────────────────────
  rawSubExpirationDate = computed(() => {
    return this.company()?.customerSubscription?.expirationDate || null;
  });

  subExpirationDate = computed(() => {
    const raw = this.rawSubExpirationDate();
    if (!raw) return null;
    return new Date(raw);
  });

  subDaysRemaining = computed<number | null>(() => {
    const raw = this.rawSubExpirationDate();
    if (!raw) return null;
    const expStr = typeof raw === 'string' ? raw.substring(0, 10) : new Date(raw).toISOString().substring(0, 10);
    const parts = expStr.split('-').map(Number);
    if (parts.length !== 3) return null;
    
    const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffTime = expDate.getTime() - todayDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  subAlertLevel = computed<'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL' | null>(() => {
    if (this.isSuperAdmin()) return null;
    if (this.isSuspended()) return 'CRITICAL';
    const days = this.subDaysRemaining();
    if (days === null) return null;
    if (days <= 0) return 'CRITICAL';
    if (days <= 5) return 'URGENT';
    if (days <= 15) return 'WARNING';
    if (days <= 30) return 'INFO';
    return null;
  });

  subDismissed = signal(false);

  // ─── 2. TOKEN DELEGADO SIAT ──────────────────────────────────
  rawSiatTokenEndDate = computed(() => {
    return this.company()?.tokenEndDate || null;
  });

  siatTokenEndDate = computed(() => {
    const raw = this.rawSiatTokenEndDate();
    if (!raw) return null;
    return new Date(raw);
  });

  siatTokenDaysRemaining = computed<number | null>(() => {
    const raw = this.rawSiatTokenEndDate();
    if (!raw) return null;
    const expStr = typeof raw === 'string' ? raw.substring(0, 10) : new Date(raw).toISOString().substring(0, 10);
    const parts = expStr.split('-').map(Number);
    if (parts.length !== 3) return null;
    
    const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffTime = expDate.getTime() - todayDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  siatTokenAlertLevel = computed<'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL' | null>(() => {
    if (this.isSuperAdmin()) return null;
    const days = this.siatTokenDaysRemaining();
    if (days === null) return null;
    if (days <= 0) return 'CRITICAL';
    if (days <= 5) return 'URGENT';
    if (days <= 15) return 'WARNING';
    return null;
  });

  siatTokenDismissed = signal(false);

  // ─── 3. FIRMA DIGITAL (SOLO ELECTRONIC_ONLINE) ───────────────
  isElectronicModality = computed(() => {
    return this.company()?.modality === 'ELECTRONIC_ONLINE';
  });

  certEndDate = computed(() => {
    if (!this.isElectronicModality()) return null;
    const endStr = this.profileService.certificate()?.endDate;
    if (!endStr) return null;
    return new Date(endStr);
  });

  certDaysRemaining = computed<number | null>(() => {
    if (!this.isElectronicModality()) return null;
    const endStr = this.profileService.certificate()?.endDate;
    if (!endStr) return null;
    const expStr = typeof endStr === 'string' ? endStr.substring(0, 10) : new Date(endStr).toISOString().substring(0, 10);
    const parts = expStr.split('-').map(Number);
    if (parts.length !== 3) return null;

    const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const diffTime = expDate.getTime() - todayDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });

  certAlertLevel = computed<'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL' | null>(() => {
    if (this.isSuperAdmin() || !this.isElectronicModality()) return null;
    const days = this.certDaysRemaining();
    if (days === null) return null;
    if (days <= 0) return 'CRITICAL';
    if (days <= 5) return 'URGENT';
    if (days <= 15) return 'WARNING';
    return null;
  });

  certDismissed = signal(false);

  // ─── 4. ALERTA ACTIVA Y PRIORIZADA PARA BANNER ───────────────
  activeAlert = computed(() => {
    if (this.isSuperAdmin()) return null;

    const certLvl = this.certAlertLevel();
    const certDays = this.certDaysRemaining();
    const showCert = certLvl && (!this.certDismissed() || certLvl === 'CRITICAL' || certLvl === 'URGENT');

    const tokenLvl = this.siatTokenAlertLevel();
    const tokenDays = this.siatTokenDaysRemaining();
    const showToken = tokenLvl && (!this.siatTokenDismissed() || tokenLvl === 'CRITICAL' || tokenLvl === 'URGENT');

    const subLvl = this.subAlertLevel();
    const subDays = this.subDaysRemaining();
    const showSub = subLvl && (!this.subDismissed() || subLvl === 'CRITICAL' || subLvl === 'URGENT');

    const activeList: Array<{
      type: 'DIGITAL_SIGNATURE' | 'SIAT_TOKEN' | 'SUBSCRIPTION';
      level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL';
      days: number;
      weight: number;
    }> = [];

    const weights: Record<string, number> = { CRITICAL: 4, URGENT: 3, WARNING: 2, INFO: 1 };

    if (showCert) activeList.push({ type: 'DIGITAL_SIGNATURE', level: certLvl!, days: certDays!, weight: weights[certLvl!] });
    if (showToken) activeList.push({ type: 'SIAT_TOKEN', level: tokenLvl!, days: tokenDays!, weight: weights[tokenLvl!] });
    if (showSub) activeList.push({ type: 'SUBSCRIPTION', level: subLvl!, days: subDays!, weight: weights[subLvl!] });

    if (activeList.length === 0) return null;

    activeList.sort((a, b) => {
      if (b.weight !== a.weight) return b.weight - a.weight;
      return a.days - b.days;
    });

    const top = activeList[0];
    if (top.type === 'DIGITAL_SIGNATURE') {
      return this.createCertAlertPayload(top.level, top.days);
    } else if (top.type === 'SIAT_TOKEN') {
      return this.createTokenAlertPayload(top.level, top.days);
    } else {
      return this.createSubAlertPayload(top.level, top.days);
    }
  });

  planName = computed(() => {
    return this.company()?.customerSubscription?.plan?.name 
      || this.company()?.subscription?.name 
      || 'Suscripción SIAT';
  });

  private createCertAlertPayload(level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL', days: number) {
    const compName = this.company()?.companyName || 'mi empresa';
    const nit = this.company()?.nit ? ` (NIT: ${this.company()?.nit})` : '';
    const phone = this.user()?.businessWhatsapp || '59179746003';
    const dateStr = this.certEndDate() ? this.formatDate(this.certEndDate()!) : '';

    let text = '';
    if (level === 'CRITICAL') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Certificado de Firma Digital vencido y requiero asistencia para renovarlo e instalarlo ante el SIAT.`;
    } else if (level === 'URGENT') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Firma Digital por vencer en ${days} día(s) (el ${dateStr}) y requiero asistencia para la renovación de firma electrónica.`;
    } else {
      text = `Hola, mi empresa ${compName}${nit} tiene su Firma Digital por vencer en ${days} días y deseo coordinar su renovación ante ADSIB/proveedor.`;
    }

    return {
      type: 'DIGITAL_SIGNATURE' as const,
      level,
      daysRemaining: days,
      expirationDate: this.certEndDate(),
      whatsappUrl: `https://wa.me/${phone}?text=${encodeURIComponent(text)}`,
      isDismissible: level === 'WARNING' || level === 'INFO'
    };
  }

  private createTokenAlertPayload(level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL', days: number) {
    const compName = this.company()?.companyName || 'mi empresa';
    const nit = this.company()?.nit ? ` (NIT: ${this.company()?.nit})` : '';
    const phone = this.user()?.businessWhatsapp || '59179746003';
    const dateStr = this.siatTokenEndDate() ? this.formatDate(this.siatTokenEndDate()!) : '';

    let text = '';
    if (level === 'CRITICAL') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Token SIAT vencido y requiero soporte para actualizarlo ante el SIN.`;
    } else if (level === 'URGENT') {
      text = `Hola, mi empresa ${compName}${nit} tiene su Token SIAT por vencer en ${days} día(s) (el ${dateStr}) y requiero asistencia para delegar el nuevo token ante el SIN.`;
    } else {
      text = `Hola, mi empresa ${compName}${nit} tiene su Token SIAT por vencer en ${days} días y requiero asistencia para renovarlo ante el SIN.`;
    }

    return {
      type: 'SIAT_TOKEN' as const,
      level,
      daysRemaining: days,
      expirationDate: this.siatTokenEndDate(),
      whatsappUrl: `https://wa.me/${phone}?text=${encodeURIComponent(text)}`,
      isDismissible: level === 'WARNING' || level === 'INFO'
    };
  }

  private createSubAlertPayload(level: 'INFO' | 'WARNING' | 'URGENT' | 'CRITICAL', days: number) {
    const compName = this.company()?.companyName || 'mi empresa';
    const nit = this.company()?.nit ? ` (NIT: ${this.company()?.nit})` : '';
    const phone = this.user()?.businessWhatsapp || '59179746003';
    const dateStr = this.subExpirationDate() ? this.formatDate(this.subExpirationDate()!) : '';

    let text = '';
    if (level === 'CRITICAL') {
      text = `Hola, mi cuenta ${compName}${nit} se encuentra ${this.isSuspended() ? 'suspendida' : 'vencida'} por falta de pago y deseo regularizar mi suscripción.`;
    } else if (level === 'URGENT') {
      text = `Hola, mi cuenta ${compName}${nit} vence en ${days} día(s) (el ${dateStr}) y deseo renovar mi suscripción con urgencia.`;
    } else {
      text = `Hola, mi cuenta ${compName}${nit} vence en ${days} días y deseo gestionar la renovación de mi suscripción.`;
    }

    return {
      type: 'SUBSCRIPTION' as const,
      level,
      daysRemaining: days,
      expirationDate: this.subExpirationDate(),
      whatsappUrl: `https://wa.me/${phone}?text=${encodeURIComponent(text)}`,
      isDismissible: level === 'WARNING' || level === 'INFO'
    };
  }

  private formatDate(d: Date): string {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${d.getFullYear()}`;
  }

  openRefreshTokenModal(): void {
    this.profileService.showModalToken = true;
  }

  dismiss(): void {
    const current = this.activeAlert();
    if (!current) return;
    if (current.type === 'DIGITAL_SIGNATURE') {
      this.certDismissed.set(true);
    } else if (current.type === 'SIAT_TOKEN') {
      this.siatTokenDismissed.set(true);
    } else {
      this.subDismissed.set(true);
    }
  }
}
